import { LayerOrientation, PcbNodeTypes, TraceShapeType, createNodeRuleSet, createVirtualRouteSegmentNode, createVirtualViaNode, getLayerIdentifier, matchLayerIdentifier, } from "@buildwithflux/models";
import { humanizeLengthValue, humanizeVectorValue, pickValues } from "@buildwithflux/shared";
import { difference } from "lodash";
import { Vector2 } from "three";
import { PcbNodeHelper, addPcbNodes, removeNodesAndDescendants } from "../../../../../nodes";
import { getProjectedChildrenPositions, getSmartViaTraceChildrenLayers, getStackupLayersAndViaConfigs, getViaSegmentResolvedSizeValues, isSubSampledViaConfig, isViaSegmentConnectingLayer, } from "../../../../../smart_via";
export default class SmartViaVirtualNodeGenerator {
    treeManager;
    stackupInfoCache = new Map();
    constructor(treeManager) {
        this.treeManager = treeManager;
    }
    /**
     * Generate virtual nodes for a smartVia node (vias and traces).
     * @param node Unbaked smartVia node
     * @param bakedRules Baked rules for the smartVia node from the deriver
     * @returns Virtual nodes to be added and removed from the tree
     */
    generate(node, bakedRules) {
        // 1. Create virtual children node objects
        // These are created on every bake, but updated to the the tree
        // on as-needed basis
        const virtualChildrenNodes = this.createVirtualChildrenNodes(node, bakedRules);
        // 2. Update tree with virtual children nodes
        // The method returns the nodes that were removed from the tree
        const nodesToRemove = this.updateTreeWithVirtualChildrenNodes(node, virtualChildrenNodes);
        // Note: Returning this object doesn't have any consequence since we're already updating
        // the tree ourselves in the `updateTreeWithVirtualChildrenNodes` method.
        // Moreover, it seems that, except for ONE place, the `nodesToAdd` isn't used anywhere.
        // We should really re-think how we handle virtual nodes cuz it gets very complicated very fast,
        // since we're passing this object up the stream multiple times via multiple returns.
        return {
            nodesToAdd: virtualChildrenNodes,
            nodesToRemove: nodesToRemove,
            ruleSetsToAdd: {},
            ruleSetsToRemove: [],
        };
    }
    createVirtualChildrenNodes(smartViaNode, bakedRules) {
        const { copperLayers, viaConfigsMap, viaHoleConfigs } = getStackupLayersAndViaConfigs(bakedRules, this.treeManager.treeNodes, this.stackupInfoCache);
        // These return the staggered positions for via placements. Right now, we're only
        // supporting the "zig-zag" stagger pattern. In the future, we may want to support
        // other patterns, so this is where we would want to make changes
        const projectedChildrenPositions = getProjectedChildrenPositions(smartViaNode, bakedRules);
        // Create virtual traces nodes at projected positions
        const virtualTraceNodes = this.createVirtualTraceNodes(smartViaNode, bakedRules, copperLayers, projectedChildrenPositions);
        // Create virtual via nodes at projected positions
        const virtualViaNodes = this.createVirtualViaNodes(smartViaNode, bakedRules, copperLayers, viaConfigsMap, viaHoleConfigs, projectedChildrenPositions);
        return [...virtualViaNodes, ...virtualTraceNodes];
    }
    createVirtualTraceNodes(smartViaNode, bakedRules, copperLayers, [position1, position2]) {
        const { effectiveNetId } = bakedRules;
        if (!effectiveNetId) {
            // Traces cannot (??) exist if the smart via doesn't belong to a Net. This sc
            return [];
        }
        // Get net layer orientation to determine whether to invert position.x values
        // TODO: consider replacing `effectiveNetId` with `smartViaNode.parentUid`
        const smartViaNet = this.treeManager.treeNodes[effectiveNetId];
        if (smartViaNet?.type !== PcbNodeTypes.net || !PcbNodeHelper.isBakedNode(smartViaNet)) {
            return [];
        }
        // QUESTION: why is bakedRules sometimes undefined???
        // Because treeManager.treeNodes are mixed type; baked and unbaked nodes - there's no contractual guarantee that a node is baked
        const netLayer = copperLayers.find((layer) => matchLayerIdentifier(layer, smartViaNet.bakedRules.layer));
        const netLayerOrientation = netLayer?.orientation ?? LayerOrientation.top;
        const traceLayers = getSmartViaTraceChildrenLayers(bakedRules.selectedViaConfigs, copperLayers);
        const virtualTraceNodes = traceLayers.map((layer) => {
            const layerId = getLayerIdentifier(layer);
            const traceLayerOrientation = layer.orientation ?? LayerOrientation.top;
            const parentBottom = netLayerOrientation === LayerOrientation.bottom;
            const traceBottom = traceLayerOrientation === LayerOrientation.bottom;
            const invertLayer = (parentBottom && !traceBottom) || (!parentBottom && traceBottom) ? -1 : 1;
            const uid = `${smartViaNode.uid}.Trace.${layerId}`;
            const startId = `${uid}_start`;
            const endId = `${uid}_end`;
            const startPosition = new Vector2(position1.x * invertLayer, position1.y);
            const endPosition = new Vector2(position2.x * invertLayer, position2.y);
            const virtualTraceNode = createVirtualRouteSegmentNode("smartVia", uid, startId, endId, startPosition, endPosition, smartViaNode.uid, layerId, undefined, undefined, layerId, undefined, undefined, 
            // Enforce straight shapes for smart via connecting traces
            {
                traceShape: TraceShapeType.straight,
                traceShapeStart: TraceShapeType.straight,
                traceShapeEnd: TraceShapeType.straight,
            });
            return virtualTraceNode;
        });
        return virtualTraceNodes;
    }
    createVirtualViaNodes(smartViaNode, bakedRules, copperLayers, viaConfigsMap, viaHoleConfigs, [position1, position2]) {
        const smartViaUid = smartViaNode.uid;
        const flattenedSegments = bakedRules.selectedViaConfigs.flatMap((viaConfig) => viaConfig.segments);
        const virtualViaNodes = bakedRules.selectedViaConfigs.flatMap((viaConfig, configIndex) => {
            const segments = viaConfig.segments.map((segment, segmentIndex) => {
                const thisSegmentIndex = flattenedSegments.indexOf(segment);
                const prevSegment = flattenedSegments[thisSegmentIndex - 1];
                const nextSegment = flattenedSegments[thisSegmentIndex + 1];
                let originalSegmentIndex = segmentIndex;
                // If this segment is coming from a sub-sampled via config, we should use the _original_
                // via segment in the UI (name)
                if (isSubSampledViaConfig(viaConfig)) {
                    const foundIndex = viaConfigsMap[viaConfig.parentConfigUid]?.segments.findIndex((parentSegment) => parentSegment.key === segment.key);
                    if (foundIndex !== undefined && foundIndex !== -1) {
                        originalSegmentIndex = foundIndex;
                    }
                }
                const uid = `${smartViaNode.uid}.${viaConfig.uid}.${configIndex}-${segmentIndex}`;
                const name = `${viaConfig.name} - ${originalSegmentIndex + 1}`;
                const viaType = `${viaConfig.uid}-${segmentIndex + 1}`;
                // Get all layers this segment can be connected to
                const segmentConnectedLayers = copperLayers
                    .filter((layer, index) => {
                    const layerOrder = index + 1; // Layer order is 1-based
                    // First check whether this layer can even be connected to this segment...
                    if (!isViaSegmentConnectingLayer(segment, layerOrder)) {
                        return false;
                    }
                    // Next, we want to check if this segment is connecting to
                    // another segment (stacked or staggered) on this layer.
                    // If yes, we necessarily include it.
                    if ((prevSegment && layerOrder === segment.startLayer) ||
                        (nextSegment && layerOrder === segment.endLayer)) {
                        return true;
                    }
                    // Finally we check if this layer is included in the smart via's connected layers rule
                    return bakedRules.connectedLayers.some((layerIdentifier) => matchLayerIdentifier(layer, layerIdentifier));
                })
                    .map(getLayerIdentifier);
                // Get the resolved segment size/holeSize/minSize/minHoleSize values
                const resolvedSizeValues = getViaSegmentResolvedSizeValues(segment, viaHoleConfigs, bakedRules.unit);
                // Create object-specific rule set for this via node
                const ruleSet = createNodeRuleSet({
                    position: humanizeVectorValue(humanizeLengthValue, configIndex % 2 === 0 ? position1 : position2, bakedRules.unit ?? "mm"),
                    viaType,
                    layer: segmentConnectedLayers[0], // The first layer is the associated layer for each via segment
                    connectedLayers: segmentConnectedLayers.join(","),
                    ...resolvedSizeValues,
                });
                return createVirtualViaNode(name, smartViaUid, smartViaUid, "smartVia", {
                    uid,
                    parentUid: smartViaUid,
                    pcbNodeRuleSet: ruleSet,
                });
            });
            return segments;
        });
        return virtualViaNodes;
    }
    /**
     * Update the tree with the virtual children nodes.
     * @param smartViaNode SmartVia node
     * @param virtualChildrenNodes Virtual children nodes to update the tree with
     * @returns The nodes that were added, removed, and updated
     */
    updateTreeWithVirtualChildrenNodes(smartViaNode, virtualChildrenNodes) {
        const treeNodes = this.treeManager.treeNodes; // TODO: bad, consider this.treeManager.getBakedNodes()
        // First, remove all existing virtual nodes that whose parent is this smart via
        // Using `smartViaNode.childUids` won't work here always since it can be empty, but the tree
        // might still have its children! Thus we're forced to go through all nodes and do the check.
        // Maybe there should be a re-parenting step at some prior point?
        const existingVirtualChildNodeUids = [];
        for (const nodeUid in treeNodes) {
            const node = treeNodes[nodeUid];
            if (node?.parentUid === smartViaNode.uid && PcbNodeHelper.isVirtualNode(node)) {
                existingVirtualChildNodeUids.push(nodeUid);
            }
        }
        const nodeUidsToRemove = difference(existingVirtualChildNodeUids, virtualChildrenNodes.map((node) => node.uid));
        const nodesToRemove = pickValues(treeNodes, nodeUidsToRemove);
        removeNodesAndDescendants(treeNodes, existingVirtualChildNodeUids);
        // Next, add the new virtual nodes to the smart via
        if (virtualChildrenNodes.length) {
            addPcbNodes(this.treeManager.treeNodes, virtualChildrenNodes, true);
        }
        return nodesToRemove;
    }
}
//# sourceMappingURL=SmartViaVirtualNodeGenerator.js.map